import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { allPages, toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { useNavigatorStore } from '../../zustand/useNavigatorStore';
import { MapboxMap } from '../MapboxMap';
import { beaconFillColor, DEFAULT_BEACON_FILL } from './beacon-colors';
import { focusBounds, WeightedPoint } from './map-focus';
import {
  hasNavigatorQuery,
  navigatorPeopleConditions,
  navigatorRules,
  parseNavigatorQuery,
} from './navigator-query';

/**
 * The Navigator's Map view: the map, shaded by how many people the panel's query (?q=) has at
 * each district, and fitted to where they are once counted. The counts come from the headcounts
 * search with the query's people conditions; they reveal no one, so a query is counted as soon
 * as it is in the URL. A query that expands from beacons draws its radius around their
 * institutions.
 */
export function NavigatorMap() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const q = params.get('q') ?? '';
  const hasQuery = hasNavigatorQuery(location.search);
  const headcountsQueryKey = useNavigatorStore((state) => state.headcountsQueryKey);
  const setQueryHeadcounts = useNavigatorStore((state) => state.setQueryHeadcounts);
  const setIsCounting = useNavigatorStore((state) => state.setIsCountingHeadcounts);
  const clearHeadcounts = useNavigatorStore((state) => state.clearHeadcounts);
  const setHighlighted = useNavigatorStore((state) => state.setHighlightedInstitutionIds);
  const setHighlightFills = useNavigatorStore((state) => state.setHighlightFills);
  const setBeaconFill = useNavigatorStore((state) => state.setBeaconFill);
  const setHighlightedRadius = useNavigatorStore((state) => state.setHighlightedRadiusMiles);

  const clearHighlight = () => {
    setHighlighted([]);
    setHighlightFills({});
    setBeaconFill(DEFAULT_BEACON_FILL);
    setHighlightedRadius(0);
  };

  // The searched radius around the searched beacons' institutions, each beacon's in its color
  // (the first's where two share one, and for the radius); none without a radius.
  useEffect(() => {
    const rules = navigatorRules(parseNavigatorQuery(location.search));
    const radius = Number(rules.find((r) => r.field === 'radiusMiles')?.value ?? 0);
    const beaconIds = rules
      .filter((r) => r.field === 'beaconIds')
      .flatMap((r) => r.value.split(','))
      .map((id) => id.trim())
      .filter((id) => !!id);
    if (radius <= 0 || beaconIds.length === 0) {
      clearHighlight();
      return;
    }

    let current = true;
    const highlight = async () => {
      try {
        const [members, beacons] = await Promise.all([
          Promise.all(
            beaconIds.map((beacon_id) =>
              allPages(eduleadV4.beacons.listInstitutions({ beacon_id, page_size: 100 })),
            ),
          ),
          Promise.all(beaconIds.map((id) => eduleadV4.beacons.get({ id }))),
        ]);
        if (!current) return;
        const fills: Record<string, string> = {};
        members.forEach((page, i) => {
          const fill = beaconFillColor(beacons[i].beacon?.color);
          page.forEach((m) => {
            if (m.id && !fills[m.id]) fills[m.id] = fill;
          });
        });
        setHighlighted(Object.keys(fills));
        setHighlightFills(fills);
        setBeaconFill(beaconFillColor(beacons[0]?.beacon?.color));
        setHighlightedRadius(radius);
      } catch (error) {
        if (current) toastV4Error(error, "Failed to load the beacons' institutions");
      }
    };
    highlight();
    return () => {
      current = false;
    };
  }, [q]);

  // Leaving the map takes the radius and its colors with it.
  useEffect(() => clearHighlight, []);

  useEffect(() => {
    if (!hasQuery) {
      clearHeadcounts();
      return;
    }
    // The counts of this query are on the map already (back from a list view).
    if (headcountsQueryKey === q) return;

    let current = true;
    const count = async () => {
      try {
        setIsCounting(true);
        const conditions = navigatorPeopleConditions(parseNavigatorQuery(location.search));
        const first = await eduleadV4.headcounts.search({
          conditions: JSON.stringify(conditions),
          page_size: 500,
        });
        // The totals are over every page; read them before paging moves the response on. The
        // API sends its int64s as strings.
        const totals = {
          people: Number(first.response.person_total ?? 0),
          accounts: Number(first.response.total_size ?? 0),
        };
        const counts: Record<string, number> = {};
        const points: WeightedPoint[] = [];
        for await (const h of first) {
          if (!h.account_id) continue;
          const count = Number(h.person_count ?? 0);
          counts[h.account_id] = count;
          points.push({ lat: h.latitude ?? 0, lng: h.longitude ?? 0, weight: count });
        }
        if (current) setQueryHeadcounts(q, conditions, counts, totals, focusBounds(points));
      } catch (error) {
        if (current) toastV4Error(error, 'Failed to count the people on the map');
      } finally {
        if (current) setIsCounting(false);
      }
    };
    count();
    // A newer query replaces this one's counts.
    return () => {
      current = false;
    };
  }, [q, hasQuery]);

  return <MapboxMap />;
}
